app.service("PracticeService", [
	"$http",
	"ENV",
	"Session",
	"$q",
	function ($http, ENV, Session, $q) {
		function resolvePracticeId(practiceId) {
			return practiceId || (Session.practice && Session.practice.id);
		}

		function missingPracticeResponse() {
			return $q.resolve({ data: {} });
		}

		var service = {
			isProcessing: false,

			getAvailablePractices: function () {
				return $http({
					method: "GET",
					url: ENV.API + "/api/practices/summary",
					headers: {
						Authorization: "Bearer " + Session.token,
					},
				});
			},
			queryUserSetting: function () {
				if (!Session.practice || !Session.practice.id) {
					return missingPracticeResponse();
				}
				return $http({
					method: "GET",
					url: ENV.API + `/api/practices/${Session.practice.id}`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
				});
			},
			updateCalendarSetting: function (data, _practiceId) {
				const practiceId = resolvePracticeId(_practiceId);
				if (!practiceId) {
					// A write must not report success when nothing was saved.
					return $q.reject({ status: 400, data: { message: "No practice selected" } });
				}
				return $http({
					method: "PUT",
					url: ENV.API + `/api/practices/${practiceId}/calendar`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					},
					data,
				});
			},
			queryCalendarSetting: function (_practiceId) {
				const practiceId = resolvePracticeId(_practiceId);
				if (!practiceId) {
					return missingPracticeResponse();
				}
				return $http({
					method: "GET",
					url: ENV.API + `/api/practices/${practiceId}/calendar`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					},
				});
			},
			// The practice's lens dispensing extras and their prices, or the defaults when it has saved none.
			queryLensDispensingSettings: function (_practiceId) {
				const practiceId = resolvePracticeId(_practiceId);
				if (!practiceId) {
					return missingPracticeResponse();
				}
				return $http({
					method: "GET",
					url: ENV.API + `/api/practices/${practiceId}/lens-dispensing-settings`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					},
				});
			},
			// Saves them. Each list is merged into the practice's current one: an item left out is kept
			// but no longer offered, and a new item without an id is given one.
			updateLensDispensingSettings: function (data, _practiceId) {
				const practiceId = resolvePracticeId(_practiceId);
				if (!practiceId) {
					// A write must not report success when nothing was saved.
					return $q.reject({ status: 400, data: { message: "No practice selected" } });
				}
				return $http({
					method: "PUT",
					url: ENV.API + `/api/practices/${practiceId}/lens-dispensing-settings`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					},
					data,
				});
			},
			// The Xero sales account and tax rate the practice invoices with by default, as saved - either
			// may be unset, and then 200 and the country's GST on sales apply.
			queryBillingDefaults: function (_practiceId) {
				const practiceId = resolvePracticeId(_practiceId);
				if (!practiceId) {
					return missingPracticeResponse();
				}
				return $http({
					method: "GET",
					url: ENV.API + `/api/practices/${practiceId}/billing-defaults`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					},
				});
			},
			// Saves them. A blank value is saved as unset.
			updateBillingDefaults: function (data, _practiceId) {
				const practiceId = resolvePracticeId(_practiceId);
				if (!practiceId) {
					return $q.reject({ status: 400, data: { message: "No practice selected" } });
				}
				return $http({
					method: "PUT",
					url: ENV.API + `/api/practices/${practiceId}/billing-defaults`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					},
					data,
				});
			},
			// Saves the practice's HPI facility ID, sent to labs with an eOrder. A blank one clears it;
			// one another practice holds is refused with a 409.
			updateHpiFacility: function (data, _practiceId) {
				const practiceId = resolvePracticeId(_practiceId);
				if (!practiceId) {
					return $q.reject({ status: 400, data: { message: "No practice selected" } });
				}
				return $http({
					method: "PUT",
					url: ENV.API + `/api/practices/${practiceId}/hpi-facility`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					},
					data,
				});
			},
			// Every room in the practice, archived ones included, in list order.
			getRooms: function (_practiceId) {
				const practiceId = resolvePracticeId(_practiceId);
				if (!practiceId) {
					return $q.resolve({ data: [] });
				}
				return $http({
					method: "GET",
					url: ENV.API + `/api/practices/${practiceId}/rooms`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					},
				});
			},
			// Adding or updating a room answers with the practice's whole room list.
			addRoom: function (room, _practiceId) {
				const practiceId = resolvePracticeId(_practiceId);
				if (!practiceId) {
					return $q.reject({ status: 400, data: { message: "No practice selected" } });
				}
				return $http({
					method: "POST",
					url: ENV.API + `/api/practices/${practiceId}/rooms`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					},
					data: room,
				});
			},
			updateRoom: function (room, _practiceId) {
				const practiceId = resolvePracticeId(_practiceId);
				if (!practiceId) {
					return $q.reject({ status: 400, data: { message: "No practice selected" } });
				}
				return $http({
					method: "PUT",
					url: ENV.API + `/api/practices/${practiceId}/rooms/${room.room_id}`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					},
					data: room,
				});
			},
			// Which rooms are in use from start to end (ISO, UTC): times and clinicians only, no patient details.
			getRoomBookings: function (_practiceId, startDateUTC, endDateUTC) {
				const practiceId = resolvePracticeId(_practiceId);
				if (!practiceId) {
					return $q.resolve({ data: [] });
				}
				return $http({
					method: "GET",
					url: ENV.API + `/api/practices/${practiceId}/rooms/bookings`,
					params: { startDateUTC, endDateUTC },
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					},
				});
			},
			// The public holidays the practice observes from start to end (yyyy-MM-dd, inclusive) - national,
			// and in Australia its state's.
			queryPublicHolidays: function (_practiceId, start, end) {
				const practiceId = resolvePracticeId(_practiceId);
				if (!practiceId) {
					return missingPracticeResponse();
				}
				return $http({
					method: "GET",
					url: ENV.API + `/api/practices/${practiceId}/holidays?start=${start}&end=${end}`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					},
				});
			},
			// The practice's leave - times the whole practice takes no bookings, such as lunch or a holiday closure.
			queryPracticeLeave: function (_practiceId) {
				const practiceId = resolvePracticeId(_practiceId);
				if (!practiceId) {
					return $q.resolve({ data: [] });
				}
				return $http({
					method: "GET",
					url: ENV.API + `/api/practices/${practiceId}/leave`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					},
				});
			},
			// Replaces all of the practice's leave. Practice Admins only.
			savePracticeLeave: function (_practiceId, leave) {
				const practiceId = resolvePracticeId(_practiceId);
				if (!practiceId) {
					return $q.reject({ status: 400, data: { message: "No practice selected" } });
				}
				return $http({
					method: "PUT",
					url: ENV.API + `/api/practices/${practiceId}/leave`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					},
					data: leave,
				});
			},
			// The upcoming appointments that fall inside leave that has not been saved yet.
			queryPracticeLeaveAffectedAppointments: function (_practiceId, leave) {
				const practiceId = resolvePracticeId(_practiceId);
				if (!practiceId) {
					return $q.reject({ status: 400, data: { message: "No practice selected" } });
				}
				return $http({
					method: "POST",
					url: ENV.API + `/api/practices/${practiceId}/leave/affected-appointments`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					},
					data: leave,
				});
			},
			// The blocks of time the practice's leave covers between two UTC instants, for the calendar.
			queryPracticeLeaveOccurrences: function (_practiceId, startDateUTC, endDateUTC) {
				const practiceId = resolvePracticeId(_practiceId);
				if (!practiceId) {
					return $q.resolve({ data: [] });
				}
				return $http({
					method: "GET",
					url: ENV.API + `/api/practices/${practiceId}/leave/occurrences?startDateUTC=${encodeURIComponent(startDateUTC)}&endDateUTC=${encodeURIComponent(endDateUTC)}`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					},
				});
			},
			querySMSAudit: function (_practiceId, start,end){
				const practiceId = resolvePracticeId(_practiceId);
				if (!practiceId) {
					return missingPracticeResponse();
				}
				return $http({
					method: "GET",
					url: ENV.API + `/api/chart/practice/${practiceId}/sms?startDateUTC=${encodeURIComponent(start)}&endDateUTC=${encodeURIComponent(end)}`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					},
				});
			},
			searchPractices: function(searchTerm) {
				if (!Session.practice || !Session.practice.id) {
					return missingPracticeResponse();
				}
				return $http({
					method: "GET",
					url: ENV.API + `/api/practices/search?searchTerm=${encodeURIComponent(searchTerm || "")}`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
				});
			},
			searchPracticeDirectory: function (terms) {
				if (!Session.practice || !Session.practice.id) {
					return missingPracticeResponse();
				}
				return $http({
					method: "GET",
					url: ENV.API + `/api/practice-directory?searchTerm=${encodeURIComponent(terms || "")}`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
				});
			},
		};

		return service;
	},
]);